import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
    Box,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useCanAccess } from 'react-admin';
import { useNavigate } from 'react-router-dom';

import { formatDate } from '@/utils/date_utils';
import type { GlobalSearchItem } from './globalSearchApi';
import { phaseLabel, phaseRoute } from './phaseRoutes';
import {
    GLOBAL_SEARCH_MIN_CHARS,
    useDebouncedGlobalSearch,
} from './useDebouncedGlobalSearch';

export { phaseLabel, phaseRoute, projectPhasePath } from './phaseRoutes';

const TOUCH_TARGET_SX = {
    '@media (hover: none) and (pointer: coarse)': {
        minHeight: 48,
        minWidth: 48,
    },
} as const;

export function resultDateLabel(
    item: Pick<GlobalSearchItem, 'phase' | 'approved_at' | 'created_at'>
): string {
    const source = item.phase === 'active' ? item.approved_at : item.created_at;
    return formatDate(source, 'MM/dd/yyyy HH:mm') || '—';
}

const GlobalSearch = () => {
    const navigate = useNavigate();
    const { canAccess } = useCanAccess({
        resource: 'projects',
        action: 'list',
    });
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);
    const { results, loading } = useDebouncedGlobalSearch(query, open);

    useEffect(() => {
        if (open) {
            const t = setTimeout(() => inputRef.current?.focus(), 50);
            return () => clearTimeout(t);
        }
        setQuery('');
        return undefined;
    }, [open]);

    if (!canAccess) return null;

    const handleClose = () => setOpen(false);

    const handleSelect = (item: GlobalSearchItem) => {
        handleClose();
        navigate(phaseRoute(item));
    };

    return (
        <>
            <Tooltip title="Search projects">
                <IconButton
                    color="inherit"
                    aria-label="Search projects"
                    onClick={() => setOpen(true)}
                    size="small"
                    sx={TOUCH_TARGET_SX}
                >
                    <SearchIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                maxWidth="sm"
                aria-label="Global project search"
                transitionDuration={0}
                slotProps={{
                    backdrop: {
                        sx: { backgroundColor: 'rgba(15, 23, 42, 0.55)' },
                    },
                }}
                PaperProps={{
                    sx: {
                        mt: { xs: 4, sm: 8 },
                        alignSelf: 'flex-start',
                        borderRadius: 2,
                    },
                }}
            >
                <Box
                    px={2}
                    pt={2}
                    pb={1}
                    display="flex"
                    alignItems="flex-start"
                    gap={1}
                >
                    <TextField
                        inputRef={inputRef}
                        fullWidth
                        autoFocus
                        placeholder="Search by project no, contractor, owner, address…"
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        inputProps={{ 'aria-label': 'Search query' }}
                    />
                    <IconButton
                        aria-label="Close search"
                        onClick={handleClose}
                        size="small"
                        sx={TOUCH_TARGET_SX}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
                <Box px={2} pb={2} minHeight={80} maxHeight={360} overflow="auto">
                    {loading && (
                        <Box display="flex" justifyContent="center" py={2}>
                            <CircularProgress size={24} />
                        </Box>
                    )}
                    {!loading &&
                        query.trim().length >= GLOBAL_SEARCH_MIN_CHARS &&
                        results.length === 0 && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                px={1}
                                py={2}
                            >
                                No results
                            </Typography>
                        )}
                    {!loading &&
                        results.map(item => (
                            <Card
                                key={item.id}
                                variant="outlined"
                                sx={{ mb: 1 }}
                            >
                                <CardActionArea
                                    onClick={() => handleSelect(item)}
                                    aria-label={`Open project ${item.project_no ?? item.id}`}
                                >
                                    <CardContent
                                        sx={{
                                            py: 1.25,
                                            '&:last-child': { pb: 1.25 },
                                        }}
                                    >
                                        <Typography variant="subtitle2">
                                            #{item.project_no ?? '—'} ·{' '}
                                            {phaseLabel(item.phase)}
                                        </Typography>
                                        <Typography variant="body2">
                                            {item.address}
                                            {item.is_legacy && (
                                                <Chip
                                                    label="Legacy"
                                                    size="small"
                                                    color="warning"
                                                    sx={{ ml: 0.5 }}
                                                />
                                        )}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                        >
                                            {item.contractor_name || '—'}
                                            {' · '}
                                            {item.creator_name || '—'}
                                            {' · '}
                                            {resultDateLabel(item)}
                                        </Typography>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        ))}
                </Box>
            </Dialog>
        </>
    );
};

export default GlobalSearch;
